import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  IconButton,
  Divider,
  Checkbox,
  useTheme,
  Typography,
} from '@mui/material';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import PercentIcon from '@mui/icons-material/Percent';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CloseIcon from '@mui/icons-material/Close';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { currencySymbol } from 'utils/constant';

import CustomTypography from 'components/Fileds/CustomTypography';
import CustomTextField from 'components/Fileds/CustomTextFiled';
import CustomButton from 'components/Fileds/CustomButton';
import CustomSelect from 'components/Fileds/CustomSelect';

const getItemTotalPriceWithAddons = (item) => {
  const basePrice = parseFloat(item?.unit_price || 0);
  const addonsTotal = (item?.orderItemAddons || []).reduce(
    (acc, addon) => acc + parseFloat(addon?.addon_price || 0),
    0
  );
  const qty = parseInt(item?.quantity || 1, 10);
  return (basePrice + addonsTotal) * qty;
};

const SplitBillModal = ({ open, onClose, orderDetails, onConfirmSplit, isLoading, disableClose }) => {
  const theme = useTheme();
  const vendorData = useSelector((state) => state?.posData?.vendor);
  const symbol = currencySymbol(vendorData?.currency);

  const existingSplitsForLock = orderDetails?.orderSplits || orderDetails?.order_splits || orderDetails?.splits || orderDetails?.orderSplit || [];
  const itemsListForLock = orderDetails?.orderItems || orderDetails?.items || orderDetails?.order_items || [];

  const hasExistingSplit = React.useMemo(() => {
    return existingSplitsForLock.length > 0;
  }, [existingSplitsForLock]);

  const isUnassignedNewItems = React.useMemo(() => {
    if (!existingSplitsForLock.length || !itemsListForLock.length) return false;
    const totalOrderItemQty = itemsListForLock.reduce((sum, item) => sum + parseInt(item.quantity || item.qty || 1, 10), 0);
    const orderTotal = parseFloat(orderDetails?.total_amount || 0);
    const splitType = existingSplitsForLock[0]?.split_type || 'item_wise';

    if (splitType === 'item_wise') {
      let totalSplitItemQty = 0;
      existingSplitsForLock.forEach(split => {
        const splitItems = split.splitItems || split.orderSplitItems || split.items || split.order_split_items || [];
        splitItems.forEach(si => {
          totalSplitItemQty += parseInt(si.quantity || si.item_quantity || si.qty || 0, 10);
        });
      });
      return totalOrderItemQty > totalSplitItemQty;
    } else {
      const totalSplitBillsSum = existingSplitsForLock.reduce((sum, s) => sum + parseFloat(s.total_amount || s.sub_total || 0), 0);
      return (orderTotal - totalSplitBillsSum > 0.5);
    }
  }, [orderDetails, existingSplitsForLock, itemsListForLock]);

  const shouldDisableClose = disableClose !== undefined ? disableClose : isUnassignedNewItems;

  // Modal Flow Step: 1 = Initial Guest Setup, 2 = Main Tabs Split Config
  const [modalStep, setModalStep] = useState(1);
  const [activeTab, setActiveTab] = useState('item_wise'); // 'item_wise' | 'equal' | 'amount_percent'

  useEffect(() => {
    if (hasExistingSplit && modalStep === 1) {
      setModalStep(2);
    }
  }, [hasExistingSplit, modalStep]);

  // Step 1 State: Guests setup
  const [guestCountInput, setGuestCountInput] = useState(orderDetails?.guest_count || 2);
  const [guestSetupList, setGuestSetupList] = useState([]);

  // Step 2 Item-wise state (Item QTY per guest allocation)
  // Structure: { [itemId]: { [personIdx]: quantityAllocated } }
  const [itemPersonQtyMap, setItemPersonQtyMap] = useState({});

  // Equals state
  const [equalGuests, setEqualGuests] = useState([]);
  const [equalBillsSummary, setEqualBillsSummary] = useState([]);

  // Amount / Percentage state
  const [splitMode, setSplitMode] = useState('Percentage'); // 'Percentage' | 'Amount'
  const [amountSplitCountInput, setAmountSplitCountInput] = useState(2);
  const [amountSplitRows, setAmountSplitRows] = useState([]);

  const totalAmount = parseFloat(orderDetails?.total_amount || 0);

  // Derive locked state: once any split is paid, the split type cannot be changed
  const hasPaidSplit = React.useMemo(() => {
    const existingSplits = orderDetails?.orderSplits || orderDetails?.order_splits || orderDetails?.splits || orderDetails?.orderSplit || [];
    return existingSplits.some((s) => s.payment_status === 'paid');
  }, [orderDetails]);

  // The split type that is locked (derived from first existing split record)
  const lockedSplitTab = React.useMemo(() => {
    const existingSplits = orderDetails?.orderSplits || orderDetails?.order_splits || orderDetails?.splits || orderDetails?.orderSplit || [];
    if (existingSplits.length === 0) return null;
    const raw = existingSplits[0]?.split_type || 'item_wise';
    return raw === 'percentage' || raw === 'amount' ? 'amount_percent' : raw;
  }, [orderDetails]);

  // Unpaid persons list derived from existing splits (for locked mode)
  const unpaidGuestList = React.useMemo(() => {
    const existingSplits = orderDetails?.orderSplits || orderDetails?.order_splits || orderDetails?.splits || orderDetails?.orderSplit || [];
    return existingSplits
      .map((s, i) => ({ ...s, originalIdx: i }))
      .filter((s) => s.payment_status !== 'paid');
  }, [orderDetails]);

  // Prevent scroll wheel from changing number input values
  const handleWheel = (e) => {
    e.target.blur();
  };

  // Helper to generate Step 1 default guest rows
  const generateStep1GuestRows = (count) => {
    const validCount = Math.max(parseInt(count, 10) || 2, 2);
    const dividedTotal = (totalAmount / validCount).toFixed(2);

    const rows = Array.from({ length: validCount }, (_, i) => ({
      name: `Person-${i + 1}`,
      amount: parseFloat(dividedTotal),
    }));

    setGuestSetupList(rows);
  };

  // Calculate maximum guest capacity based on table capacity
  const maxGuestCapacity = React.useMemo(() => {
    const mainCap = parseInt(
      orderDetails?.tableInfo?.capacity ||
      orderDetails?.table?.capacity ||
      orderDetails?.table_capacity ||
      orderDetails?.capacity ||
      0,
      10
    );

    const childTables = orderDetails?.tableInfo?.childTables || orderDetails?.table?.childTables || [];
    const childCap = childTables.reduce((acc, child) => acc + parseInt(child?.capacity || 0, 10), 0);

    const totalCap = mainCap + childCap;
    return totalCap > 0 ? totalCap : null;
  }, [orderDetails]);

  const guestAvatarColors = [
    { bg: '#fee2e2', color: '#ef4444' },
    { bg: '#dcfce7', color: '#16a34a' },
    { bg: '#f3e8ff', color: '#9333ea' },
    { bg: '#dbeafe', color: '#2563eb' },
    { bg: '#ffedd5', color: '#ea580c' },
    { bg: '#fef3c7', color: '#d97706' },
    { bg: '#e0e7ff', color: '#4f46e5' },
    { bg: '#ccfbf1', color: '#0d9488' },
  ];

  const updateGuestRows = (newCount) => {
    setGuestSetupList((prevList) => {
      const dividedTotal = parseFloat((totalAmount / newCount).toFixed(2));
      if (newCount === prevList.length) return prevList;

      if (newCount > prevList.length) {
        const added = Array.from({ length: newCount - prevList.length }, (_, i) => ({
          name: `Person-${prevList.length + i + 1}`,
          amount: dividedTotal,
        }));
        const updated = [...prevList, ...added];
        return updated.map((item) => ({ ...item, amount: dividedTotal }));
      } else {
        const trimmed = prevList.slice(0, newCount);
        return trimmed.map((item) => ({ ...item, amount: dividedTotal }));
      }
    });
  };

  const handleGuestInputChange = (val) => {
    const cleanVal = String(val).replace(/\D/g, '');
    setGuestCountInput(cleanVal);

    if (cleanVal === '') return;

    let parsed = parseInt(cleanVal, 10);
    if (isNaN(parsed)) return;

    if (parsed >= 2) {
      updateGuestRows(parsed);
    }
  };

  const handleGuestInputBlur = () => {
    let parsed = parseInt(guestCountInput, 10);
    if (isNaN(parsed) || parsed < 2) {
      parsed = 2;
    }
    setGuestCountInput(parsed);
    updateGuestRows(parsed);
  };

  const handleIncrementGuest = () => {
    const current = parseInt(guestCountInput, 10) || 2;
    const nextCount = current + 1;
    setGuestCountInput(nextCount);
    updateGuestRows(nextCount);
  };

  const handleDecrementGuest = () => {
    const current = parseInt(guestCountInput, 10) || 2;
    const nextCount = Math.max(2, current - 1);
    setGuestCountInput(nextCount);
    updateGuestRows(nextCount);
  };

  // Initialize modal state on open
  useEffect(() => {
    if (open && orderDetails) {
      console.log('=== SPLIT BILL MODAL OPENED ===');
      console.log('orderDetails:', orderDetails);
      const existingSplits = orderDetails?.orderSplits || orderDetails?.order_splits || orderDetails?.splits || orderDetails?.orderSplit || [];
      console.log('existingSplits:', existingSplits);
      const hasExistingSplit = existingSplits.length > 0;
      console.log('hasExistingSplit:', hasExistingSplit);

      if (hasExistingSplit) {
        // Edit Mode: Directly open Step 2 tabs modal
        setModalStep(2);
        const existingTab = existingSplits[0]?.split_type || 'item_wise';
        setActiveTab(existingTab === 'percentage' || existingTab === 'amount' ? 'amount_percent' : existingTab);

        // Populate guest names from existing splits
        const guestNames = existingSplits.map((s, i) => s.guest_name || `Person-${i + 1}`);
        const count = Math.max(guestNames.length, 2);
        setGuestCountInput(count);

        const guestRows = Array.from({ length: count }, (_, i) => ({
          name: guestNames[i] || `Person-${i + 1}`,
          amount: parseFloat((totalAmount / count).toFixed(2))
        }));
        setGuestSetupList(guestRows);

        // Calculate total amount already paid in existing paid splits
        let paidAmountSum = 0;
        const paidGuestsMap = {};
        existingSplits.forEach((s) => {
          if (s.payment_status === 'paid') {
            paidAmountSum += parseFloat(s.total_amount || s.sub_total || 0);
            if (s.guest_name) paidGuestsMap[s.guest_name.toLowerCase().trim()] = true;
          }
        });

        const remainingBalance = Math.max(0, totalAmount - paidAmountSum);
        const unpaidSplits = existingSplits.filter(s => s.payment_status !== 'paid');
        const unpaidCount = Math.max(unpaidSplits.length, 1);

        // Equals tab setup for remaining balance or updated total
        if (paidAmountSum > 0 && unpaidSplits.length > 0) {
          const baseAmt = parseFloat((remainingBalance / unpaidCount).toFixed(2));
          const lastAmt = parseFloat((remainingBalance - baseAmt * (unpaidCount - 1)).toFixed(2));
          setEqualGuests(unpaidSplits.map((s, i) => ({
            name: s.guest_name || `Person-${i + 1}`,
            amount: i === unpaidCount - 1 ? lastAmt : baseAmt
          })));
        } else {
          const baseAmt = parseFloat((totalAmount / count).toFixed(2));
          const lastAmt = parseFloat((totalAmount - baseAmt * (count - 1)).toFixed(2));
          setEqualGuests(existingSplits.map((s, i) => ({
            name: s.guest_name || `Person-${i + 1}`,
            amount: i === count - 1 ? lastAmt : baseAmt
          })));
        }

        // Amount/Percentage setup
        setAmountSplitCountInput(count);
        if (existingTab === 'percentage' || existingTab === 'amount') {
          const isPct = existingTab === 'percentage';
          setSplitMode(isPct ? 'Percentage' : 'Amount');

          if (paidAmountSum > 0 && unpaidSplits.length > 0) {
            const basePct = parseFloat((100 / unpaidCount).toFixed(1));
            const lastPct = parseFloat((100 - basePct * (unpaidCount - 1)).toFixed(1));
            const baseAmt = parseFloat(((remainingBalance * basePct) / 100).toFixed(2));
            const lastAmt = parseFloat((remainingBalance - baseAmt * (unpaidCount - 1)).toFixed(2));

            setAmountSplitRows(unpaidSplits.map((s, i) => {
              const isLast = i === unpaidCount - 1;
              return {
                name: s.guest_name || `Person-${i + 1}`,
                value: isPct ? (isLast ? lastPct : basePct) : parseFloat((remainingBalance / unpaidCount).toFixed(2)),
                calculatedAmount: (isLast ? lastAmt : baseAmt).toFixed(2)
              };
            }));
          } else {
            // ALL SPLITS ARE UNPAID (paidAmountSum === 0)
            if (isPct) {
              const existingPctSum = existingSplits.reduce((sum, s) => sum + (parseFloat(s.split_value) || 0), 0);
              let runningAmtSum = 0;

              const rows = existingSplits.map((s, i) => {
                const isLast = i === existingSplits.length - 1;
                const pct = (existingPctSum > 99 && existingPctSum < 101)
                  ? parseFloat(s.split_value)
                  : (isLast ? parseFloat((100 - parseFloat((100 / count).toFixed(1)) * (count - 1)).toFixed(1)) : parseFloat((100 / count).toFixed(1)));

                let calcAmt;
                if (isLast) {
                  calcAmt = Math.max(0, parseFloat((totalAmount - runningAmtSum).toFixed(2)));
                } else {
                  calcAmt = parseFloat(((totalAmount * pct) / 100).toFixed(2));
                  runningAmtSum += calcAmt;
                }

                return {
                  name: s.guest_name || `Person-${i + 1}`,
                  value: pct,
                  calculatedAmount: calcAmt.toFixed(2)
                };
              });
              setAmountSplitRows(rows);
            } else {
              // Amount mode when ALL splits are unpaid
              const oldSum = existingSplits.reduce((sum, s) => sum + (parseFloat(s.split_value || s.total_amount || 0)), 0);
              let runningAmtSum = 0;

              const rows = existingSplits.map((s, i) => {
                const isLast = i === existingSplits.length - 1;
                let amt;
                if (oldSum > 0) {
                  const ratio = (parseFloat(s.split_value || s.total_amount || 0)) / oldSum;
                  if (isLast) {
                    amt = Math.max(0, parseFloat((totalAmount - runningAmtSum).toFixed(2)));
                  } else {
                    amt = parseFloat((totalAmount * ratio).toFixed(2));
                    runningAmtSum += amt;
                  }
                } else {
                  const baseAmt = parseFloat((totalAmount / count).toFixed(2));
                  amt = isLast ? Math.max(0, parseFloat((totalAmount - baseAmt * (count - 1)).toFixed(2))) : baseAmt;
                }

                return {
                  name: s.guest_name || `Person-${i + 1}`,
                  value: amt,
                  calculatedAmount: amt.toFixed(2)
                };
              });
              setAmountSplitRows(rows);
            }
          }
        } else {
          setSplitMode('Percentage');
          if (paidAmountSum > 0 && unpaidSplits.length > 0) {
            const basePct = parseFloat((100 / unpaidCount).toFixed(1));
            const lastPct = parseFloat((100 - basePct * (unpaidCount - 1)).toFixed(1));
            const baseAmt = parseFloat(((remainingBalance * basePct) / 100).toFixed(2));
            const lastAmt = parseFloat((remainingBalance - baseAmt * (unpaidCount - 1)).toFixed(2));

            setAmountSplitRows(unpaidSplits.map((s, i) => {
              const isLast = i === unpaidCount - 1;
              return {
                name: s.guest_name || `Person-${i + 1}`,
                value: isLast ? lastPct : basePct,
                calculatedAmount: (isLast ? lastAmt : baseAmt).toFixed(2)
              };
            }));
          } else {
            const basePct = parseFloat((100 / count).toFixed(1));
            const lastPct = parseFloat((100 - basePct * (count - 1)).toFixed(1));
            const baseAmt = parseFloat(((totalAmount * basePct) / 100).toFixed(2));
            const lastAmt = parseFloat((totalAmount - baseAmt * (count - 1)).toFixed(2));

            setAmountSplitRows(guestRows.map((g, i) => {
              const isLast = i === count - 1;
              return {
                name: g.name,
                value: isLast ? lastPct : basePct,
                calculatedAmount: (isLast ? lastAmt : baseAmt).toFixed(2)
              };
            }));
          }
        }

        // Prefill Item-wise itemSplitsMap from existingSplits
        const splitsMap = {};
        const orderItemsList = orderDetails?.orderItems || orderDetails?.items || [];

        orderItemsList.forEach((item) => {
          const totalOrderedQty = parseInt(item.quantity || item.qty || 1, 10);
          const itemSubSplits = [];
          let assignedQtySum = 0;

          // Check each existing split bill record for this item
          existingSplits.forEach((splitRecord, pIdx) => {
            const splitItemsList =
              splitRecord.splitItems ||
              splitRecord.orderSplitItems ||
              splitRecord.items ||
              splitRecord.order_split_items ||
              [];

            const matchedSplitItem = splitItemsList.find((si) => {
              const siItemId = String(si.order_item_id || si.item_id || si.menu_item_id || si.id || '');
              const itemId = String(item.id || item.menu_item_id || item.order_item_id || '');
              
              if (siItemId && itemId && siItemId === itemId) return true;

              const siName = String(si.menu_item_name || si.item_name || si.name || si.menuItem?.name || '')
                .toLowerCase()
                .trim();
              const itemName = String(item.menu_item_name || item.item_name || item.name || '')
                .toLowerCase()
                .trim();

              return Boolean(siName && itemName && (siName === itemName || itemName.includes(siName) || siName.includes(itemName)));
            });

            if (matchedSplitItem) {
              const qty = parseInt(
                matchedSplitItem.quantity || matchedSplitItem.item_quantity || matchedSplitItem.qty || 0,
                10
              );
              if (qty > 0) {
                assignedQtySum += qty;
                itemSubSplits.push({
                  splitId: Math.random(),
                  qty: qty,
                  personIdx: pIdx,
                });
              }
            }
          });

          // If there is remaining unallocated quantity, append an unassigned row
          if (assignedQtySum < totalOrderedQty) {
            itemSubSplits.push({
              splitId: Math.random(),
              qty: totalOrderedQty - assignedQtySum,
              personIdx: '', // Unassigned
            });
          }

          const k1 = item.id;
          const k2 = item.menu_item_id;
          const k3 = item.order_item_id;

          if (k1) splitsMap[k1] = itemSubSplits;
          if (k2) splitsMap[k2] = itemSubSplits;
          if (k3) splitsMap[k3] = itemSubSplits;
          splitsMap[item.id || item.menu_item_id || item.order_item_id] = itemSubSplits;
        });

        console.log('Final Edit Mode itemSplitsMap generated:', splitsMap);
        setItemSplitsMap(splitsMap);

      } else {
        // First Time Split: Open Step 1 setup modal
        setModalStep(1);
        setActiveTab('item_wise');

        let initialCount = Math.max(parseInt(orderDetails.guest_count || 2, 10), 2);
        if (maxGuestCapacity && initialCount > maxGuestCapacity) {
          initialCount = maxGuestCapacity;
        }
        setGuestCountInput(initialCount);
        generateStep1GuestRows(initialCount);

        // Reset itemSplitsMap (default unassigned for each item)
        const splitsMap = {};
        const orderItemsList = orderDetails?.orderItems || orderDetails?.items || [];
        orderItemsList.forEach((item) => {
          const totalQty = parseInt(item.quantity || item.qty || 1, 10);
          const defaultSubSplit = [
            {
              splitId: 1,
              qty: totalQty,
              personIdx: '', // Unassigned
            },
          ];

          const k1 = item.id;
          const k2 = item.menu_item_id;
          const k3 = item.order_item_id;

          if (k1) splitsMap[k1] = defaultSubSplit;
          if (k2) splitsMap[k2] = defaultSubSplit;
          if (k3) splitsMap[k3] = defaultSubSplit;
          splitsMap[item.id || item.menu_item_id || item.order_item_id] = defaultSubSplit;
        });
        setItemSplitsMap(splitsMap);
      }
    }
  }, [open, orderDetails]);

  // Step 1: Update guest name
  const handleStep1NameChange = (index, value) => {
    const updated = [...guestSetupList];
    updated[index].name = value;
    setGuestSetupList(updated);
  };

  // Step 1: Click '+' button to regenerate count and divided equal values
  const handleStep1AddClick = () => {
    generateStep1GuestRows(guestCountInput);
  };

  // Transition from Step 1 -> Step 2
  const handleContinueToStep2 = () => {
    let parsedCount = parseInt(guestCountInput, 10);
    if (isNaN(parsedCount) || parsedCount < 2) {
      parsedCount = 2;
      setGuestCountInput(2);
      updateGuestRows(2);
    }

    if (guestSetupList.length < 2) {
      toast.error("Please split between at least 2 guests", { position: 'top-right' });
      return;
    }

    const count = guestSetupList.length;

    // Prefill Equals tab state from Step 1
    const equalRows = guestSetupList.map(g => ({
      name: g.name || '',
      amount: g.amount
    }));
    setEqualGuests(equalRows);

    // Prefill Amount/Percentage tab state from Step 1
    setAmountSplitCountInput(count);
    setSplitMode('Percentage');
    const basePct = parseFloat((100 / count).toFixed(1));
    const lastPct = parseFloat((100 - basePct * (count - 1)).toFixed(1));
    const baseAmt = parseFloat(((totalAmount * basePct) / 100).toFixed(2));
    const lastAmt = parseFloat((totalAmount - baseAmt * (count - 1)).toFixed(2));

    const amtRows = guestSetupList.map((g, i) => {
      const isLast = i === count - 1;
      return {
        name: g.name || `Person-${i + 1}`,
        value: isLast ? lastPct : basePct,
        calculatedAmount: (isLast ? lastAmt : baseAmt).toFixed(2)
      };
    });
    setAmountSplitRows(amtRows);

    setModalStep(2);
  };

  // -------------------------------------------------------------
  // TAB 1 (Step 2): DYNAMIC ITEM-WISE SPLIT ASSIGNMENTS
  // -------------------------------------------------------------
  // Structure: { [itemId]: [ { id, qty, personIdx } ] }
  const [itemSplitsMap, setItemSplitsMap] = useState({});

  // Helper to reconcile assigned and unassigned sub-splits for an item
  const reconcileItemSplits = (splitsList, orderedQty) => {
    let currentSplits = splitsList.map((s) => ({ ...s }));

    // Group assigned splits by personIdx to consolidate same person assignments
    const assignedMap = {};
    const assignedOrder = [];

    currentSplits.forEach((s) => {
      if (s.personIdx !== '' && s.personIdx !== undefined && s.personIdx !== null) {
        const pKey = String(s.personIdx);
        if (!assignedMap[pKey]) {
          assignedMap[pKey] = { ...s };
          assignedOrder.push(pKey);
        } else {
          assignedMap[pKey].qty += s.qty;
        }
      }
    });

    const mergedAssignedSplits = assignedOrder.map((key) => assignedMap[key]);

    const assignedSum = mergedAssignedSplits.reduce((sum, s) => sum + s.qty, 0);
    const unassignedQty = Math.max(0, orderedQty - assignedSum);

    if (unassignedQty > 0) {
      const existingUnassigned = currentSplits.find(
        (s) => s.personIdx === '' || s.personIdx === undefined || s.personIdx === null
      );

      return [
        ...mergedAssignedSplits,
        {
          splitId: existingUnassigned ? existingUnassigned.splitId : Date.now() + Math.random(),
          personIdx: '',
          qty: unassignedQty,
        },
      ];
    } else {
      return mergedAssignedSplits;
    }
  };

  // Person dropdown change handler
  const handleItemSplitPersonChange = (itemId, splitId, personIdxVal, orderedQty) => {
    setItemSplitsMap((prev) => {
      const currentSplits = [...(prev[itemId] || [])];
      const targetIdx = currentSplits.findIndex((s) => s.splitId === splitId);
      if (targetIdx === -1) return prev;

      currentSplits[targetIdx] = {
        ...currentSplits[targetIdx],
        personIdx: personIdxVal !== '' ? parseInt(personIdxVal, 10) : '',
      };

      const reconciled = reconcileItemSplits(currentSplits, orderedQty);
      return { ...prev, [itemId]: reconciled };
    });
  };

  // Quantity change handler [- QTY +]
  const handleItemSplitQtyChange = (itemId, splitId, delta, orderedQty) => {
    setItemSplitsMap((prev) => {
      let currentSplits = (prev[itemId] || []).map((s) => ({ ...s }));
      const targetIdx = currentSplits.findIndex((s) => s.splitId === splitId);
      if (targetIdx === -1) return prev;

      const targetSplit = currentSplits[targetIdx];
      const isAssigned =
        targetSplit.personIdx !== '' &&
        targetSplit.personIdx !== undefined &&
        targetSplit.personIdx !== null;
      const newQty = targetSplit.qty + delta;

      if (newQty <= 0) {
        if (isAssigned) {
          // Decreasing an assigned person's QTY to 0 removes their split row
          currentSplits.splice(targetIdx, 1);
          const reconciled = reconcileItemSplits(currentSplits, orderedQty);
          return { ...prev, [itemId]: reconciled };
        } else {
          toast.error("Quantity cannot be less than 1", { position: "top-right" });
          return prev;
        }
      }

      // Calculate total assigned QTY across DIFFERENT assigned sub-splits
      const differentPersonsAssignedSum = currentSplits.reduce((sum, s, idx) => {
        if (idx === targetIdx) return sum;
        return s.personIdx !== '' &&
          s.personIdx !== undefined &&
          s.personIdx !== null &&
          s.personIdx !== targetSplit.personIdx
          ? sum + s.qty
          : sum;
      }, 0);

      if (isAssigned && differentPersonsAssignedSum + newQty > orderedQty) {
        toast.error(`Total quantity cannot exceed ordered quantity (${orderedQty})`, {
          position: "top-right",
        });
        return prev;
      }

      if (!isAssigned && delta > 0) {
        toast.error(`Total quantity cannot exceed ordered quantity (${orderedQty})`, {
          position: "top-right",
        });
        return prev;
      }

      targetSplit.qty = newQty;
      const reconciled = reconcileItemSplits(currentSplits, orderedQty);

      return {
        ...prev,
        [itemId]: reconciled,
      };
    });
  };

  // Validation: Check if ALL items in Item-wise tab are 100% assigned to a person
  const isItemWiseFullyAssigned = () => {
    const orderItemsList = orderDetails?.orderItems || orderDetails?.items || orderDetails?.order_items || [];
    if (orderItemsList.length === 0) return false;
    for (const item of orderItemsList) {
      const itemId = item.id || item.menu_item_id || item.order_item_id;
      const splits = itemSplitsMap[itemId] || [];
      const totalOrdered = parseInt(item.quantity || item.qty || 1, 10);
      const totalAssigned = splits.reduce((sum, s) => {
        return s.personIdx !== '' && s.personIdx !== undefined && s.personIdx !== null
          ? sum + s.qty
          : sum;
      }, 0);

      if (totalAssigned !== totalOrdered) {
        return false; // Found item not fully split
      }
    }
    return true;
  };

  const isConfirmDisabled = isLoading || (activeTab === 'item_wise' && !isItemWiseFullyAssigned());

  // -------------------------------------------------------------
  // TAB 2 (Step 2): EQUALS LOGIC
  // -------------------------------------------------------------
  const generateEqualRows = (count) => {
    let validCount = Math.max(parseInt(count, 10) || 1, 1);
    const baseAmt = parseFloat((totalAmount / validCount).toFixed(2));
    // Last person absorbs any cent-level rounding difference
    const lastAmt = parseFloat((totalAmount - baseAmt * (validCount - 1)).toFixed(2));

    const rows = Array.from({ length: validCount }, (_, i) => ({
      name: `Person-${i + 1}`,
      amount: i === validCount - 1 ? lastAmt : baseAmt,
    }));

    setEqualGuests(rows);
  };

  const handleEqualNameChange = (index, value) => {
    const updated = [...equalGuests];
    updated[index].name = value;
    setEqualGuests(updated);
  };

  const handleEqualAddClick = () => {
    generateEqualRows(guestCountInput);
  };

  // -------------------------------------------------------------
  // TAB 3 (Step 2): AMOUNT / PERCENTAGE LOGIC
  // -------------------------------------------------------------
  const generateAmountPercentRows = (count, mode) => {
    let validCount = Math.max(parseInt(count, 10) || 1, 1);

    const existingSplits = orderDetails?.orderSplits || orderDetails?.order_splits || [];
    const paidSplits = existingSplits.filter(s => s.payment_status === 'paid');
    const paidAmountSum = paidSplits.reduce((sum, s) => sum + parseFloat(s.total_amount || s.sub_total || 0), 0);
    const targetTotal = paidSplits.length > 0 ? Math.max(0, totalAmount - paidAmountSum) : totalAmount;

    let rows;
    if (mode === 'Percentage') {
      // Base percentage (1 decimal display), last person gets the remainder to reach exactly 100%
      const basePct = parseFloat((100 / validCount).toFixed(1));
      const lastPct = parseFloat((100 - basePct * (validCount - 1)).toFixed(1));
      // Calculate amounts from percentages; last person gets remainder to exactly match total
      const baseAmt = parseFloat(((targetTotal * basePct) / 100).toFixed(2));
      const lastAmt = parseFloat((targetTotal - baseAmt * (validCount - 1)).toFixed(2));

      rows = Array.from({ length: validCount }, (_, i) => {
        const isLast = i === validCount - 1;
        return {
          name: `Person-${i + 1}`,
          value: isLast ? lastPct : basePct,
          calculatedAmount: (isLast ? lastAmt : baseAmt).toFixed(2),
        };
      });
    } else {
      // Amount mode: base amount rounded to 2 dp; last person absorbs rounding difference
      const baseAmt = parseFloat((targetTotal / validCount).toFixed(2));
      const lastAmt = parseFloat((targetTotal - baseAmt * (validCount - 1)).toFixed(2));

      rows = Array.from({ length: validCount }, (_, i) => {
        const isLast = i === validCount - 1;
        const amt = isLast ? lastAmt : baseAmt;
        return {
          name: `Person-${i + 1}`,
          value: amt,
          calculatedAmount: amt.toFixed(2),
        };
      });
    }

    setAmountSplitRows(rows);
  };

  const handleAmountPercentValueChange = (index, rawValue) => {
    const updated = [...amountSplitRows];
    let val = parseFloat(rawValue) || 0;

    const existingSplits = orderDetails?.orderSplits || orderDetails?.order_splits || [];
    const paidSplits = existingSplits.filter(s => s.payment_status === 'paid');
    const paidAmountSum = paidSplits.reduce((sum, s) => sum + parseFloat(s.total_amount || s.sub_total || 0), 0);
    const targetBalance = paidSplits.length > 0 ? Math.max(0, totalAmount - paidAmountSum) : totalAmount;

    if (splitMode === 'Percentage') {
      const otherSum = updated.reduce((sum, r, idx) => idx === index ? sum : sum + (parseFloat(r.value) || 0), 0);
      const maxAllowed = Math.max(100 - otherSum, 0);

      if (val > maxAllowed) {
        val = maxAllowed;
        toast.error(`Total percentage cannot exceed 100%. Max remaining is ${maxAllowed.toFixed(1)}%`, { position: 'top-right' });
      }
      updated[index].value = val;
      updated[index].calculatedAmount = ((targetBalance * val) / 100).toFixed(2);
    } else {
      const otherSum = updated.reduce((sum, r, idx) => idx === index ? sum : sum + (parseFloat(r.value) || 0), 0);
      const maxAllowed = Math.max(targetBalance - otherSum, 0);

      if (val > maxAllowed) {
        val = maxAllowed;
        toast.error(`Total amount cannot exceed ${symbol}${targetBalance.toFixed(2)}`, { position: 'top-right' });
      }
      updated[index].value = val;
      updated[index].calculatedAmount = val.toFixed(2);
    }

    setAmountSplitRows(updated);
  };

  const handleAmountPercentNameChange = (index, value) => {
    const updated = [...amountSplitRows];
    updated[index].name = value;
    setAmountSplitRows(updated);
  };

  const handleAmountPercentAddClick = () => {
    generateAmountPercentRows(amountSplitCountInput, splitMode);
  };

  // -------------------------------------------------------------
  // CONFIRM SPLIT SUBMISSION (Step 2)
  // -------------------------------------------------------------
  const handleSubmit = () => {
    let payload = { split_type: activeTab, splits: [] };

    if (activeTab === 'item_wise') {
      const personCount = guestSetupList.length;
      const personBills = Array.from({ length: personCount }, (_, pIdx) => ({
        guest_name: guestSetupList[pIdx]?.name || `Person-${pIdx + 1}`,
        items: []
      }));

      const orderItemsList = orderDetails?.orderItems || orderDetails?.items || orderDetails?.order_items || [];
      orderItemsList.forEach(item => {
        const itemId = item.id || item.menu_item_id || item.order_item_id;
        const splits = itemSplitsMap[itemId] || [];
        const baseUnitPrice = parseFloat(item?.unit_price || 0);
        const addonsTotal = (item?.orderItemAddons || []).reduce(
          (acc, addon) => acc + parseFloat(addon?.addon_price || 0),
          0
        );
        const unitTotalPrice = baseUnitPrice + addonsTotal;

        splits.forEach(s => {
          if (s.personIdx !== '' && s.personIdx !== undefined && s.personIdx !== null && s.qty > 0) {
            const pIdx = s.personIdx;
            if (personBills[pIdx]) {
              personBills[pIdx].items.push({
                order_item_id: item.id || item.order_item_id || item.menu_item_id,
                menu_item_name: item.menu_item_name || item.item_name || item.name,
                quantity: s.qty,
                unit_price: item.unit_price,
                total_price: parseFloat((unitTotalPrice * s.qty).toFixed(2))
              });
            }
          }
        });
      });

      const existingSplits = orderDetails?.orderSplits || orderDetails?.order_splits || [];
      const paidGuestsMap = {};
      existingSplits.forEach((s) => {
        if (s.payment_status === 'paid' && s.guest_name) {
          paidGuestsMap[s.guest_name.toLowerCase().trim()] = true;
        }
      });

      // Exclude paid guests from newly submitted splits so they aren't duplicated
      const validSplits = personBills.filter(
        b => b.items.length > 0 && !paidGuestsMap[b.guest_name.toLowerCase().trim()]
      );

      if (validSplits.length < 1) {
        toast.error("Please assign items to remaining unpaid persons", { position: 'top-right' });
        return;
      }

      payload.splits = validSplits;

    } else if (activeTab === 'equal') {
      const existingSplits = orderDetails?.orderSplits || orderDetails?.order_splits || [];
      const paidSplits = existingSplits.filter(s => s.payment_status === 'paid');
      const paidAmountSum = paidSplits.reduce((sum, s) => sum + parseFloat(s.total_amount || s.sub_total || 0), 0);
      const remainingBalance = totalAmount - paidAmountSum;

      const paidGuestsMap = {};
      paidSplits.forEach(s => {
        if (s.guest_name) paidGuestsMap[s.guest_name.toLowerCase().trim()] = true;
      });

      const validEqualSplits = equalGuests.filter(g => !paidGuestsMap[(g.name || '').toLowerCase().trim()]);
      if (validEqualSplits.length < 1) {
        toast.error("Please split remaining bill between guests", { position: 'top-right' });
        return;
      }

      const totalSplitEqual = validEqualSplits.reduce((sum, g) => sum + (parseFloat(g.amount) || 0), 0);
      const targetEqual = paidSplits.length > 0 ? remainingBalance : totalAmount;
      if (Math.abs(totalSplitEqual - targetEqual) > 0.5) {
        toast.error(`Total split amount must equal ${paidSplits.length > 0 ? 'remaining unpaid balance' : 'total bill'} (${symbol}${targetEqual.toFixed(2)})`, { position: 'top-right' });
        return;
      }

      payload.splits = validEqualSplits.map(g => ({
        guest_name: g.name || null,
        amount: g.amount
      }));
    } else if (activeTab === 'amount_percent') {
      const typeKey = splitMode === 'Percentage' ? 'percentage' : 'amount';
      payload.split_type = typeKey;

      const existingSplits = orderDetails?.orderSplits || orderDetails?.order_splits || [];
      const paidSplits = existingSplits.filter(s => s.payment_status === 'paid');
      const paidAmountSum = paidSplits.reduce((sum, s) => sum + parseFloat(s.total_amount || s.sub_total || 0), 0);
      const remainingBalance = totalAmount - paidAmountSum;

      const paidGuestsMap = {};
      paidSplits.forEach(s => {
        if (s.guest_name) paidGuestsMap[s.guest_name.toLowerCase().trim()] = true;
      });

      const validAmountRows = amountSplitRows.filter(r => !paidGuestsMap[(r.name || '').toLowerCase().trim()]);

      if (splitMode === 'Percentage') {
        const totalPercent = validAmountRows.reduce((sum, r) => sum + (parseFloat(r.value) || 0), 0);
        if (Math.abs(totalPercent - 100) > 0.5) {
          toast.error("Total split percentage must equal 100%", { position: 'top-right' });
          return;
        }
      } else {
        const targetAmount = paidSplits.length > 0 ? remainingBalance : totalAmount;
        const totalSplitAmount = validAmountRows.reduce((sum, r) => sum + (parseFloat(r.value) || 0), 0);
        if (Math.abs(totalSplitAmount - targetAmount) > 0.5) {
          toast.error(`Total split amount must equal ${paidSplits.length > 0 ? 'remaining unpaid balance' : 'total bill'} (${symbol}${targetAmount.toFixed(2)})`, { position: 'top-right' });
          return;
        }
      }

      payload.splits = validAmountRows.map(r => ({
        guest_name: r.name || null,
        split_value: parseFloat(r.value),
        amount: parseFloat(r.calculatedAmount)
      }));
    }

    onConfirmSplit(payload);
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (shouldDisableClose) return;
        if (onClose) onClose(event, reason);
      }}
      disableEscapeKeyDown={shouldDisableClose}
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: "16px",
          backgroundColor: "#fff",
          width: "620px",
          maxWidth: "95vw"
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          fontSize: "18px",
          textAlign: "center",
          fontWeight: 600,
          color: "#000",
          pb: 1,
          display: 'flex',
          justify: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box sx={{ flex: 1, textAlign: 'center', fontWeight: 600, fontSize: '18px' }}>
          Split Bill for Order #{orderDetails?.order_id}
        </Box>
        {!shouldDisableClose && (
          <IconButton onClick={onClose} size="small" sx={{ color: '#000' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent sx={{ pt: 1, pb: 3 }}>
        {/* Total Bill Amount Banner */}
        <Box sx={{ display: 'flex', alignItems: 'center', textAlign: 'center', mb: 1, bgcolor: '#fff', borderRadius: '12px', justifyContent: 'center' }}>
          <CustomTypography
            variant="body2"
            label="Total Bill Amount: "
            sx={{ fontSize: "14px", fontWeight: 500, color: "#4D4D4D", mb: 0, paddingRight: '10px' }}
          />
          <CustomTypography
            variant="body1"
            label={`${symbol}${totalAmount.toFixed(2)}`}
            sx={{ fontSize: "18px", fontWeight: 700, color: "#000", mb: 0 }}
          />
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* STEP 1: INITIAL GUEST SETUP MODAL */}
        {modalStep === 1 && (
          <Box>
            <CustomTypography
              label="Split Between (Number of Guests)"
              sx={{ fontWeight: 600, fontSize: '14px', color: '#000', mb: 1 }}
            />

            {/* Counter [- QTY +] */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <IconButton
                onClick={handleDecrementGuest}
                disabled={Number(guestCountInput) <= 2}
                sx={{
                  width: '46px',
                  height: '46px',
                  borderRadius: '8px',
                  bgcolor: '#fff',
                  color: '#0f172a',
                  border: '1px solid #cbd5e1',
                  '&:hover': { bgcolor: '#f8fafc' },
                  '&.Mui-disabled': { bgcolor: '#f8fafc', borderColor: '#e2e8f0', color: '#cbd5e1' }
                }}
              >
                <RemoveIcon fontSize="small" />
              </IconButton>

              <Box
                sx={{
                  flex: 1,
                  height: '46px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid #cbd5e1',
                  borderRadius: '8px',
                  px: 2,
                  bgcolor: '#fff'
                }}
              >
                <input
                  type="text"
                  pattern="[0-9]*"
                  value={guestCountInput}
                  onChange={(e) => handleGuestInputChange(e.target.value)}
                  onBlur={handleGuestInputBlur}
                  style={{
                    border: 'none',
                    outline: 'none',
                    textAlign: 'center',
                    width: '100%',
                    fontWeight: 700,
                    fontSize: '18px',
                    color: '#0f172a',
                    background: 'transparent',
                    flex: 1
                  }}
                />
              </Box>

              <IconButton
                onClick={handleIncrementGuest}
                sx={{
                  width: '46px',
                  height: '46px',
                  borderRadius: '8px',
                  bgcolor: '#fff',
                  color: '#0f172a',
                  border: '1px solid #cbd5e1',
                  '&:hover': { bgcolor: '#f8fafc' },
                  '&.Mui-disabled': { bgcolor: '#f8fafc', borderColor: '#e2e8f0', color: '#cbd5e1' }
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Guest Names Grid (Side-by-Side 2-Column Grid as Old) */}
            {guestSetupList.length > 0 && (
              <Box sx={{ bgcolor: '#fff', p: 2, borderRadius: '12px', border: '1px solid #e2e8f0', mb: 2 }}>
                <CustomTypography
                  label="Guest Names"
                  sx={{ fontWeight: 600, fontSize: '14px', color: '#000', mb: 1.5 }}
                />
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                  {guestSetupList.map((guest, idx) => (
                    <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                      <CustomTextField
                        placeholder={`Person-${idx + 1}`}
                        value={guest.name}
                        onChange={(e) => handleStep1NameChange(idx, e.target.value)}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Action Buttons Step 1: Close and Continue */}
            <Box className="d-flex align-items-center justify-content-center" sx={{ gap: 2, mt: 3 }}>
              <CustomButton
                variant="outlined"
                onClick={onClose}
                sx={{
                  px: 3,
                  fontSize: "14px",
                  borderColor: "#000",
                  color: "#000",
                  backgroundColor: "#fff",
                  borderRadius: '10px',
                  height: '45px',
                  width: '120px'
                }}
              >
                Close
              </CustomButton>

              <CustomButton
                onClick={handleContinueToStep2}
                sx={{
                  width: '180px',
                  borderRadius: '10px',
                  height: '45px',
                  backgroundColor: '#000',
                  color: '#fff',
                  '&:hover': { backgroundColor: '#1f2937' }
                }}
              >
                Continue
              </CustomButton>
            </Box>
          </Box>
        )}

        {/* STEP 2: SPLIT TABS CONFIGURATION */}
        {modalStep === 2 && (
          <Box>
            {/* ROW 1: Split Type Tab Buttons */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1, mb: 1.5 }}>
              {[
                { key: 'item_wise', label: 'Item-wise', Icon: FormatListBulletedIcon },
                { key: 'equal', label: 'Equals', Icon: DragHandleIcon },
                { key: 'amount_percent', label: 'Amount/Percent', Icon: PercentIcon },
              ].map(({ key, label, Icon }) => {
                const isActive = activeTab === key;
                const isLocked = hasPaidSplit && lockedSplitTab !== null && key !== lockedSplitTab;
                return (
                  <CustomButton
                    key={key}
                    onClick={() => {
                      if (isLocked) return;
                      setActiveTab(key);
                    }}
                    sx={{
                      flexDirection: 'column',
                      py: 1,
                      height: 'auto',
                      borderRadius: '10px',
                      border: isActive ? 'none' : '1px solid #cbd5e1',
                      backgroundColor: isActive ? '#000' : isLocked ? '#f8fafc' : '#fff',
                      color: isActive ? '#fff' : isLocked ? '#cbd5e1' : '#4D4D4D',
                      '&:hover': {
                        backgroundColor: isLocked ? '#f8fafc' : isActive ? '#1f2937' : '#e2e8f0',
                        cursor: isLocked ? 'default' : 'pointer',
                      },
                      textTransform: 'none',
                      fontWeight: 600,
                      opacity: isLocked ? 0.4 : 1,
                      pointerEvents: isLocked ? 'none' : 'auto',
                    }}
                  >
                    <Icon sx={{ mb: 0.5, fontSize: '20px' }} />
                    <CustomTypography
                      label={label}
                      sx={{ fontSize: '11px', fontWeight: 600, color: isActive ? '#fff' : isLocked ? '#cbd5e1' : '#475569' }}
                    />
                  </CustomButton>
                );
              })}
            </Box>

            {/* ROW 2: Guest Count [- N +] — only for Equals and Amount/Percent tabs */}
            {activeTab !== 'item_wise' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <CustomTypography
                  label="Split Between (Number of Guests)"
                  sx={{ fontWeight: 600, fontSize: '14px', color: '#000', width: '100%' }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, width: '100%' }}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      if (activeTab === 'equal') {
                        const next = Math.max(2, (parseInt(guestCountInput, 10) || 2) - 1);
                        setGuestCountInput(next);
                        generateEqualRows(next);
                      } else {
                        const next = Math.max(2, (parseInt(amountSplitCountInput, 10) || 2) - 1);
                        setAmountSplitCountInput(next);
                        generateAmountPercentRows(next, splitMode);
                      }
                    }}
                    disabled={
                      hasPaidSplit ||
                      (activeTab === 'equal'
                        ? Number(guestCountInput) <= 2
                        : Number(amountSplitCountInput) <= 2)
                    }
                    sx={{
                      width: '34px',
                      height: '34px',
                      borderRadius: '8px',
                      bgcolor: '#fff',
                      color: '#0f172a',
                      border: '1px solid #cbd5e1',
                      '&:hover': { bgcolor: '#f8fafc' },
                      '&.Mui-disabled': { bgcolor: '#f8fafc', borderColor: '#e2e8f0', color: '#cbd5e1' },
                    }}
                  >
                    <RemoveIcon sx={{ fontSize: '16px' }} />
                  </IconButton>

                  <Box
                    sx={{
                      width: '48px',
                      height: '34px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid #cbd5e1',
                      borderRadius: '8px',
                      bgcolor: hasPaidSplit ? '#f8fafc' : '#fff',
                      flex: 1
                    }}
                  >
                    <input
                      type="text"
                      pattern="[0-9]*"
                      readOnly={hasPaidSplit}
                      value={activeTab === 'equal' ? guestCountInput : amountSplitCountInput}
                      onChange={(e) => {
                        if (hasPaidSplit) return;
                        const val = e.target.value.replace(/\D/g, '');
                        const n = parseInt(val, 10);
                        if (activeTab === 'equal') {
                          setGuestCountInput(val);
                          if (!isNaN(n) && n >= 2) generateEqualRows(n);
                        } else {
                          setAmountSplitCountInput(val);
                          if (!isNaN(n) && n >= 2) generateAmountPercentRows(n, splitMode);
                        }
                      }}
                      onBlur={() => {
                        if (hasPaidSplit) return;
                        if (activeTab === 'equal') {
                          const n = Math.max(2, parseInt(guestCountInput, 10) || 2);
                          setGuestCountInput(n);
                          generateEqualRows(n);
                        } else {
                          const n = Math.max(2, parseInt(amountSplitCountInput, 10) || 2);
                          setAmountSplitCountInput(n);
                          generateAmountPercentRows(n, splitMode);
                        }
                      }}
                      style={{
                        border: 'none',
                        outline: 'none',
                        textAlign: 'center',
                        width: '100%',
                        fontWeight: 700,
                        fontSize: '15px',
                        color: hasPaidSplit ? '#94a3b8' : '#0f172a',
                        background: 'transparent',
                        cursor: hasPaidSplit ? 'not-allowed' : 'text',
                      }}
                    />
                  </Box>

                  <IconButton
                    size="small"
                    onClick={() => {
                      if (activeTab === 'equal') {
                        const next = (parseInt(guestCountInput, 10) || 2) + 1;
                        setGuestCountInput(next);
                        generateEqualRows(next);
                      } else {
                        const next = (parseInt(amountSplitCountInput, 10) || 2) + 1;
                        setAmountSplitCountInput(next);
                        generateAmountPercentRows(next, splitMode);
                      }
                    }}
                    disabled={hasPaidSplit}
                    sx={{
                      width: '34px',
                      height: '34px',
                      borderRadius: '8px',
                      bgcolor: '#fff',
                      color: '#0f172a',
                      border: '1px solid #cbd5e1',
                      '&:hover': { bgcolor: '#f8fafc' },
                      '&.Mui-disabled': { bgcolor: '#f8fafc', borderColor: '#e2e8f0', color: '#cbd5e1' },
                    }}
                  >
                    <AddIcon sx={{ fontSize: '16px' }} />
                  </IconButton>
                </Box>
              </Box>
            )}

            {/* TAB 1: ITEM-WISE QTY COUNTER SPLIT */}
            {activeTab === 'item_wise' && (
              <Box>
                <CustomTypography
                  variant="caption"
                  label="* Assign item quantity to guest persons"
                  sx={{ color: '#4D4D4D', fontStyle: 'italic', display: 'block', mb: 1.5 }}
                />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
                  {(orderDetails?.orderItems || orderDetails?.items || [])
                    .filter((item) => {
                      const existingSplits = orderDetails?.orderSplits || orderDetails?.order_splits || [];
                      const hasPaidSplit = existingSplits.some((s) => s.payment_status === 'paid');
                      if (!hasPaidSplit) return true;

                      // Check if item is a new item not fully present in existing splits
                      const itemId = String(item.id || item.menu_item_id || item.order_item_id || '');
                      const totalOrdered = parseInt(item.quantity || item.qty || 1, 10);
                      
                      let splitQtySum = 0;
                      existingSplits.forEach((sp) => {
                        const spItems = sp.splitItems || sp.orderSplitItems || sp.items || [];
                        spItems.forEach((si) => {
                          const siId = String(si.order_item_id || si.item_id || si.menu_item_id || si.id || '');
                          if (siId && itemId && siId === itemId) {
                            splitQtySum += parseInt(si.quantity || si.item_quantity || si.qty || 0, 10);
                          }
                        });
                      });

                      return splitQtySum < totalOrdered;
                    })
                    .flatMap((item) => {
                      const orderedQty = parseInt(item.quantity || item.qty || 1, 10);
                      const unitPriceWithAddons = getItemTotalPriceWithAddons(item) / orderedQty;
                      const hasAddons = item.orderItemAddons && item.orderItemAddons.length > 0;
                      const subSplits = itemSplitsMap[item.id || item.menu_item_id || item.order_item_id] || [];

                      const existingSplits = orderDetails?.orderSplits || orderDetails?.order_splits || [];
                      const hasPaidSplit = existingSplits.some((s) => s.payment_status === 'paid');

                      // Filter out paid sub-splits when partially paid
                      const visibleSubSplits = hasPaidSplit
                        ? subSplits.filter((s) => {
                            if (s.personIdx === '') return true;
                            const isPaidGuest = existingSplits[s.personIdx]?.payment_status === 'paid';
                            return !isPaidGuest;
                          })
                        : subSplits;

                      return visibleSubSplits.map((subSplit) => {
                        const isUnassignedItem = subSplit.personIdx === '' || subSplit.personIdx === undefined || subSplit.personIdx === null;
                        const rowTotalPrice = (unitPriceWithAddons * subSplit.qty).toFixed(2);

                        const differentPersonsAssignedSum = subSplits.reduce((sum, s) => {
                          if (s.splitId === subSplit.splitId) return sum;
                          return s.personIdx !== '' &&
                            s.personIdx !== undefined &&
                            s.personIdx !== null &&
                            s.personIdx !== subSplit.personIdx
                            ? sum + s.qty
                            : sum;
                        }, 0);
                        const isPlusDisabled = isUnassignedItem || (subSplit.qty + differentPersonsAssignedSum >= orderedQty);

                        return (
                          <Box
                            key={`${item.id || item.menu_item_id || item.order_item_id}_${subSplit.splitId}`}
                            sx={{
                              bgcolor: '#fff',
                              p: 1.5,
                              borderRadius: '12px',
                              border: '1px solid #e2e8f0',
                            }}
                          >
                            {/* Item Name & Total Price Header */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                              <Box>
                                <CustomTypography
                                  label={`${item.menu_item_name || item.item_name || item.name}`}
                                  sx={{ fontSize: '15px', color: '#000', fontWeight: 700 }}
                                />
                                {/* Addons Display */}
                                {hasAddons && (
                                  <Box sx={{ mt: 0.2 }}>
                                    {item.orderItemAddons.map((addon, aIdx) => (
                                      <Box key={aIdx} sx={{ display: 'flex', gap: 1, py: 0.1 }}>
                                        <CustomTypography
                                          label={`+ ${addon.addon_name}`}
                                          sx={{ fontSize: '12px', color: '#4D4D4D' }}
                                        />
                                        <CustomTypography
                                          label={`${symbol}${parseFloat(addon.addon_price || 0).toFixed(2)}`}
                                          sx={{ fontSize: '12px', color: '#000' }}
                                        />
                                      </Box>
                                    ))}
                                  </Box>
                                )}
                              </Box>

                              <CustomTypography
                                label={`${symbol}${rowTotalPrice}`}
                                sx={{ fontSize: '15px', fontWeight: 700, color: '#000' }}
                              />
                            </Box>

                            {/* Person Select Dropdown + [- QTY +] Counter */}
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                py: 0.8,
                                px: 1.2,
                                borderRadius: '8px',
                                gap: 1.5
                              }}
                            >
                              {/* Person Select Dropdown */}
                              <Box sx={{ minWidth: '160px', flex: 1 }}>
                                <CustomSelect
                                  value={subSplit.personIdx !== undefined && subSplit.personIdx !== null && subSplit.personIdx !== '' ? Number(subSplit.personIdx) : ''}
                                  onChange={(e) => handleItemSplitPersonChange(item.id || item.menu_item_id || item.order_item_id, subSplit.splitId, e.target.value, orderedQty)}
                                  options={[
                                    { label: 'Select Person', value: '' },
                                    ...guestSetupList
                                      .map((g, pIdx) => {
                                        const isPaidGuest = (orderDetails?.orderSplits || [])[pIdx]?.payment_status === 'paid';
                                        return {
                                          label: isPaidGuest ? `${g.name || `Person-${pIdx + 1}`} (Paid)` : (g.name || `Person-${pIdx + 1}`),
                                          value: pIdx,
                                          disabled: isPaidGuest && subSplit.personIdx !== pIdx,
                                        };
                                      })
                                      .filter((opt) => !opt.disabled)
                                  ]}
                                  formWidth="100%"
                                  sx={{
                                    height: '34px',
                                    fontSize: '12px',
                                    '& .MuiSelect-select': { py: 0.5, px: 1 }
                                  }}
                                />
                              </Box>

                              {/* Counter [- QTY +] */}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleItemSplitQtyChange(item.id || item.menu_item_id || item.order_item_id, subSplit.splitId, -1, orderedQty)}
                                  disabled={subSplit.qty <= 1}
                                  sx={{
                                    border: '1px solid #cbd5e1',
                                    borderRadius: '6px',
                                    width: '26px',
                                    height: '26px',
                                    p: 0,
                                    bgcolor: '#fff',
                                    color: '#000',
                                    '&:hover': { bgcolor: '#f8fafc' },
                                    '&.Mui-disabled': { bgcolor: '#f8fafc', borderColor: '#e2e8f0', color: '#cbd5e1' }
                                  }}
                                >
                                  <RemoveIcon fontSize="small" sx={{ fontSize: '14px' }} />
                                </IconButton>

                                <CustomTypography
                                  label={`${subSplit.qty}`}
                                  sx={{ fontSize: '14px', fontWeight: 700, minWidth: '18px', textAlign: 'center', color: '#000' }}
                                />

                                <IconButton
                                  size="small"
                                  onClick={() => handleItemSplitQtyChange(item.id || item.menu_item_id || item.order_item_id, subSplit.splitId, 1, orderedQty)}
                                  disabled={isPlusDisabled}
                                  sx={{
                                    border: '1px solid #cbd5e1',
                                    borderRadius: '6px',
                                    width: '26px',
                                    height: '26px',
                                    p: 0,
                                    bgcolor: '#fff',
                                    color: '#000',
                                    '&:hover': { bgcolor: '#f1f5f9' },
                                    '&.Mui-disabled': {
                                      bgcolor: '#f8fafc',
                                      borderColor: '#e2e8f0',
                                      color: '#cbd5e1'
                                    }
                                  }}
                                >
                                  <AddIcon fontSize="small" sx={{ fontSize: '14px' }} />
                                </IconButton>
                              </Box>
                            </Box>
                          </Box>
                        );
                      });
                    })}
                </Box>
              </Box>
            )}
            {/* TAB 2: EQUALS */}
            {activeTab === 'equal' && (
              <Box>
                {(() => {
                  const existingSplits = orderDetails?.orderSplits || orderDetails?.order_splits || [];
                  const paidRows = existingSplits
                    .map((s, i) => ({ ...s, _origIdx: i }))
                    .filter((s) => s.payment_status === 'paid');

                  // Full list: paid guests first (read-only), then editable unpaid guests
                  const allRows = [
                    ...paidRows.map((s) => ({
                      name: s.guest_name || `Person-${s._origIdx + 1}`,
                      amount: parseFloat(s.total_amount || s.sub_total || 0),
                      isPaid: true,
                    })),
                    ...equalGuests.map((g) => ({ ...g, isPaid: false })),
                  ];

                  if (allRows.length === 0) return null;

                  return (
                    <Box sx={{ bgcolor: '#fff', p: 2, borderRadius: '12px', border: '1px solid #e2e8f0', mb: 2 }}>
                      <CustomTypography label="Guest Names" sx={{ fontWeight: 600, fontSize: '14px', color: '#000', mb: 1.5 }} />
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                        {allRows.map((row, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              p: 1,
                              border: row.isPaid ? '1px solid #bbf7d0' : '1px solid #e2e8f0',
                              borderRadius: '8px',
                              bgcolor: row.isPaid ? '#f0fdf4' : '#fff',
                              gap: 1,
                            }}
                          >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <CustomTypography
                                label={row.name}
                                sx={{ fontSize: '13px', fontWeight: 600, color: row.isPaid ? '#15803d' : '#000', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                              />
                              {row.isPaid && (
                                <CustomTypography
                                  label="Paid"
                                  sx={{ fontSize: '10px', fontWeight: 700, color: '#16a34a', lineHeight: 1.2 }}
                                />
                              )}
                            </Box>
                            <CustomTypography
                              label={`${symbol}${row.amount.toFixed(2)}`}
                              sx={{ fontSize: '13px', fontWeight: 700, color: row.isPaid ? '#15803d' : '#000', minWidth: '65px', textAlign: 'right' }}
                            />
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  );
                })()}
              </Box>
            )}

            {/* TAB 3: AMOUNT / PERCENTAGE */}
            {activeTab === 'amount_percent' && (
              <Box>
                <CustomSelect
                  value={splitMode}
                  onChange={(e) => {
                    if (hasPaidSplit) return;
                    setSplitMode(e.target.value);
                    generateAmountPercentRows(amountSplitCountInput, e.target.value);
                  }}
                  options={[
                    { label: 'Percentage (%)', value: 'Percentage' },
                    { label: `Amount (${symbol})`, value: 'Amount' },
                  ]}
                  formWidth="100%"
                  sx={{ mb: 1.5, pointerEvents: hasPaidSplit ? 'none' : 'auto', opacity: hasPaidSplit ? 0.6 : 1 }}
                />

                {(() => {
                  const existingSplits = orderDetails?.orderSplits || orderDetails?.order_splits || [];
                  const paidSplits = existingSplits
                    .map((s, i) => ({ ...s, _origIdx: i }))
                    .filter((s) => s.payment_status === 'paid');

                  // Merge: paid rows first (read-only), then editable unpaid rows
                  const allRows = [
                    ...paidSplits.map((s) => ({
                      name: s.guest_name || `Person-${s._origIdx + 1}`,
                      value: parseFloat(s.split_value || 0),
                      calculatedAmount: parseFloat(s.total_amount || s.sub_total || 0).toFixed(2),
                      isPaid: true,
                    })),
                    ...amountSplitRows.map((r) => ({ ...r, isPaid: false })),
                  ];

                  if (allRows.length === 0) return null;

                  return (
                    <Box sx={{ bgcolor: '#fff', p: 2, borderRadius: '12px', border: '1px solid #e2e8f0', mb: 2 }}>
                      <CustomTypography label="Split Breakdown" sx={{ fontWeight: 600, fontSize: '14px', color: '#000', mb: 1.5 }} />
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                        {allRows.map((row, idx) => {
                          const unpaidIdx = idx - paidSplits.length;
                          return (
                            <Box
                              key={idx}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.8,
                                p: 1,
                                border: row.isPaid ? '1px solid #bbf7d0' : '1px solid #e2e8f0',
                                borderRadius: '8px',
                                bgcolor: row.isPaid ? '#f0fdf4' : '#fff',
                              }}
                            >
                              {row.isPaid ? (
                                // Read-only paid row
                                <>
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <CustomTypography
                                      label={row.name}
                                      sx={{ fontSize: '12px', fontWeight: 600, color: '#15803d', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                    />
                                    <CustomTypography
                                      label="Paid"
                                      sx={{ fontSize: '10px', fontWeight: 700, color: '#16a34a', lineHeight: 1.2 }}
                                    />
                                  </Box>
                                  <CustomTypography
                                    label={`${symbol}${parseFloat(row.calculatedAmount || 0).toFixed(2)}`}
                                    sx={{ fontSize: '12px', fontWeight: 700, color: '#15803d', minWidth: '55px', textAlign: 'right' }}
                                  />
                                </>
                              ) : (
                                // Editable unpaid row
                                <>
                                  <Box sx={{ flex: 1 }}>
                                    <CustomTextField
                                      placeholder="Guest name"
                                      value={row.name}
                                      onChange={(e) => handleAmountPercentNameChange(unpaidIdx, e.target.value)}
                                      InputProps={{
                                        sx: {
                                          height: '38px',
                                          '& .MuiInputBase-input': {
                                            py: 0,
                                            px: '10px',
                                            fontSize: '13px',
                                          },
                                        },
                                      }}
                                    />
                                  </Box>
                                  <Box sx={{ width: '90px' }}>
                                    <CustomTextField
                                      type="number"
                                      value={row.value}
                                      onChange={(e) => handleAmountPercentValueChange(unpaidIdx, e.target.value)}
                                      onWheel={handleWheel}
                                      endIcon={
                                        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#4D4D4D' }}>
                                          {splitMode === 'Percentage' ? '%' : symbol}
                                        </Typography>
                                      }
                                      InputProps={{
                                        sx: {
                                          height: '38px',
                                          '& .MuiInputBase-input': {
                                            py: 0,
                                            pl: '10px',
                                            pr: '4px',
                                            fontSize: '13px',
                                          },
                                          '& .MuiInputAdornment-root': {
                                            ml: 0.5,
                                          },
                                        },
                                      }}
                                    />
                                  </Box>
                                  <CustomTypography
                                    label={`${symbol}${parseFloat(row.calculatedAmount || 0).toFixed(2)}`}
                                    sx={{ fontSize: '12px', fontWeight: 700, color: '#000', minWidth: '55px', textAlign: 'right' }}
                                  />
                                </>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  );
                })()}
              </Box>
            )}

            {/* Modal Step 2 Action Buttons */}
            <Box className="d-flex align-items-center justify-content-center" sx={{ gap: 2, mt: 3 }}>
              {!hasExistingSplit && (
                <CustomButton
                  variant="outlined"
                  onClick={() => {
                    if (hasExistingSplit) return;
                    setModalStep(1);
                  }}
                  sx={{
                    px: 3,
                    fontSize: "14px",
                    borderColor: "#000",
                    color: "#000",
                    backgroundColor: "#fff",
                    borderRadius: '10px',
                    height: '45px',
                    width: '120px'
                  }}
                >
                  Back
                </CustomButton>
              )}

              <CustomButton
                disabled={isConfirmDisabled}
                onClick={handleSubmit}
                sx={{
                  width: '180px',
                  borderRadius: '10px',
                  height: '45px',
                  backgroundColor: '#000',
                  color: '#fff',
                  '&:hover': { backgroundColor: '#1f2937' },
                  '&.Mui-disabled': { backgroundColor: '#cbd5e1', color: '#94a3b8' }
                }}
              >
                {isLoading ? 'Processing...' : 'Confirm Split'}
              </CustomButton>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SplitBillModal;